import { useId, useMemo, useState } from 'react';

import { Select } from '@/components/Select';
import {
  useAiProvider,
  useSaveAiProvider,
  useTestAiProvider,
  type AdminAiProvider,
  type AiProviderName,
} from '@/lib/ai-provider';
import { saveMessage } from '@/lib/apps';
import { moment } from '@/lib/dates';
import { useSeeded } from '@/lib/seeded';

/**
 * The language-model provider the admin talks to: which one, how to reach it,
 * and the key — entered here, sealed on the server, never shown again.
 *
 * ⚠️ **The key box is always empty, and that is not a bug to fix.** The server
 * returns the stored key's last four characters and nothing else, so there is
 * nothing to put in the box; the tail is shown beside it instead. Typing into
 * the box replaces the key on the next Save; leaving it alone keeps whatever is
 * stored. "Remove" sends an explicit null. An empty box is never sent — a form
 * re-posting its blank field must not wipe a key somebody entered last month.
 *
 * ⚠️ **Three states the pill has to tell apart**, because they look identical
 * from the outside ("there is a key, nothing works"):
 *   - a key is stored and the server can open it        → "key set · …0Ae"
 *   - a key is stored and the server cannot open it     → the encryption
 *     variable has changed since; enter the key again
 *   - the server has no encryption variable at all      → nothing can be
 *     stored; set it and restart
 * Nothing at all when no key has ever been entered — anh Duy chốt: a status
 * that has nothing to report says nothing.
 */

/** Shown to a person; the server's own names are the keys. */
const PROVIDERS: Array<[AiProviderName, string]> = [
  ['anthropic', 'Anthropic · Claude'],
  ['openai', 'OpenAI'],
  ['gemini', 'Google · Gemini'],
  ['ollama', 'Ollama'],
  ['openrouter', 'OpenRouter'],
  ['custom', 'Custom provider'],
];
const providerLabel = (name: AiProviderName): string =>
  PROVIDERS.find(([key]) => key === name)?.[1] ?? name;
const providerNamed = (label: string): AiProviderName =>
  PROVIDERS.find(([, text]) => text === label)?.[0] ?? 'custom';

/**
 * ⚠️ **Only Anthropic gets a list, and the list is a stop-gap.** These are the
 * current ids of the one provider this studio actually uses. Every other
 * provider gets a text box: offering somebody a dropdown of names this admin
 * cannot vouch for is worse than asking them to type the one they mean. When
 * there is an API to ask each provider for its models, both go away.
 */
const CLAUDE_MODELS = ['claude-sonnet-5', 'claude-opus-5', 'claude-haiku-4-5'] as const;

/** Where a provider answers by default — mirrored from the server for the placeholder only. */
const DEFAULT_BASE_URL: Partial<Record<AiProviderName, string>> = {
  anthropic: 'https://api.anthropic.com/v1',
  openai: 'https://api.openai.com/v1',
  gemini: 'https://generativelanguage.googleapis.com/v1beta',
  openrouter: 'https://openrouter.ai/api/v1',
};

/** Keep a credential out of the browser's own stores — see `ConnectionsPage`. */
const SECRET_FIELD = {
  autoComplete: 'new-password',
  spellCheck: false,
  autoCorrect: 'off',
  autoCapitalize: 'off',
} as const;

interface Draft {
  provider: AiProviderName;
  baseUrl: string;
  model: string;
  /** What is in the box. Empty means "do not touch the stored key". */
  apiKey: string;
}

const draftOf = (saved: AdminAiProvider): Draft => ({
  provider: saved.provider,
  // A default address is shown as the placeholder, not as a value, so the
  // box reads as "leave blank for the provider's own".
  baseUrl: saved.baseUrlIsDefault ? '' : saved.baseUrl,
  model: saved.model,
  apiKey: '',
});

export function AiProviderCard() {
  const provider = useAiProvider();
  const save = useSaveAiProvider();
  const test = useTestAiProvider();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [keyShown, setKeyShown] = useState(false);
  const [removeKey, setRemoveKey] = useState(false);
  /** Bumped on every save, so the form re-seeds whole and the key box empties. */
  const [saves, setSaves] = useState(0);

  const headingId = useId();
  const providerLabelId = useId();
  const modelLabelId = useId();
  const apiKeyField = useId();
  const apiKeyNote = useId();
  const baseUrlNote = useId();
  const testResultId = useId();

  /*
   * ⚠️ Seeded per field, not replaced whole — `useSeeded`, the same hook both
   * editors use and for the same reason. A plain effect on `provider.data`
   * re-seeds on every new object, and a background refetch of this query is
   * a new object: whoever was halfway through typing a key would watch the
   * box empty. `refetchOnWindowFocus` is off in `main.tsx` today, which hid
   * it; the first `invalidateQueries` from elsewhere (P2 will need to read
   * this row) would have found it. Per field, a refetch that changed nothing
   * touches nothing, and `apiKey` — always `''` in the baseline — is left as
   * typed.
   *
   * `saves` is the one case that *should* re-seed whole: after a save the key
   * went to the server and the box must go blank.
   */
  const baseline = useMemo(() => (provider.data ? draftOf(provider.data) : null), [provider.data]);
  useSeeded(baseline, setDraft, String(saves));

  if (provider.isPending || !draft) {
    return (
      <section aria-labelledby={headingId} className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <h2 id={headingId} className="eyebrow m-0">
          AI provider
        </h2>
        <p className="m-0 text-[13px]" style={{ color: 'var(--muted)' }}>
          Loading…
        </p>
      </section>
    );
  }
  if (provider.isError) {
    return (
      <section aria-labelledby={headingId} className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <h2 id={headingId} className="eyebrow m-0">
          AI provider
        </h2>
        <p className="m-0 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
          {saveMessage(provider.error)}
        </p>
      </section>
    );
  }

  const saved = provider.data;
  const dirty =
    draft.provider !== baseline?.provider ||
    draft.baseUrl.trim() !== baseline?.baseUrl ||
    draft.model.trim() !== baseline?.model ||
    draft.apiKey.trim() !== '' ||
    removeKey;

  const canStoreKey = !saved.storageUnavailable;
  const keyStored = saved.apiKeyLast4 !== null;
  const keyUnreadable = keyStored && !saved.configured && canStoreKey;

  const onSave = () => {
    const typed = draft.apiKey.trim();
    // A result from before this save describes a configuration that no longer
    // exists; "answered in 0.8s" under a changed provider is a false report.
    test.reset();
    save.mutate(
      {
        provider: draft.provider,
        baseUrl: draft.baseUrl.trim() === '' ? null : draft.baseUrl.trim(),
        model: draft.model.trim(),
        /*
         * Three meanings, and the empty box is deliberately none of them: the
         * field is simply left out, and the server keeps what it has.
         */
        ...(removeKey ? { apiKey: null } : typed ? { apiKey: typed } : {}),
      },
      {
        onSuccess: () => {
          setRemoveKey(false);
          setKeyShown(false);
          setSaves((count) => count + 1);
        },
      },
    );
  };

  const pickProvider = (name: AiProviderName) =>
    setDraft((current) =>
      current
        ? {
            ...current,
            provider: name,
            // The base URL is left exactly as it is: blank still means "the
            // provider's own", which follows the provider, and a typed one is
            // somebody's proxy and stays.
            // Claude ids are only right for Anthropic.
            model:
              name === 'anthropic'
                ? (CLAUDE_MODELS as readonly string[]).includes(current.model)
                  ? current.model
                  : CLAUDE_MODELS[0]
                : (CLAUDE_MODELS as readonly string[]).includes(current.model)
                  ? ''
                  : current.model,
          }
        : current,
    );

  return (
    <section aria-labelledby={headingId} className="panel flex flex-col gap-2.5 px-4 py-3.5">
      <span className="flex flex-wrap items-center gap-2.5">
        <h2 id={headingId} className="eyebrow m-0">
          AI provider
        </h2>
        {saved.configured && keyStored ? (
          <Pill tone="ok">key set · …{saved.apiKeyLast4}</Pill>
        ) : keyUnreadable ? (
          <Pill tone="warn">key unreadable</Pill>
        ) : null}
        {saved.updatedAt ? (
          <span className="ml-auto font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
            saved {moment(saved.updatedAt)}
            {saved.updatedByName ? ` by ${saved.updatedByName}` : ''}
          </span>
        ) : null}
      </span>

      <p className="m-0 text-[11.5px] leading-[1.55]" style={{ color: 'var(--muted)' }}>
        Used by Translate with AI, the Signals summaries on Overview and anything else in the
        admin that calls a language model.
      </p>

      {!canStoreKey ? (
        <Notice tone="warn">
          This server has no <code className="font-mono">SECRETS_ENCRYPTION_KEY</code>, so a key
          cannot be stored yet. Set one (<code className="font-mono">openssl rand -base64 32</code>)
          in <code className="font-mono">apps/api/.env.local</code> and restart. Provider, address
          and model can be saved now.
        </Notice>
      ) : keyUnreadable ? (
        <Notice tone="warn">
          A key is stored but this server cannot open it —{' '}
          <code className="font-mono">SECRETS_ENCRYPTION_KEY</code> has changed since it was saved.
          Enter the key again.
        </Notice>
      ) : null}

      <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <div className="flex min-w-0 flex-col gap-1.5">
          <span id={providerLabelId} className="field-label">
            Provider
          </span>
          <Select
            value={providerLabel(draft.provider)}
            options={PROVIDERS.map(([, label]) => label)}
            onChange={(label) => pickProvider(providerNamed(label))}
            labelledBy={providerLabelId}
          />
          <span className="text-[11px] leading-[1.45]" style={{ color: 'var(--dim)' }}>
            One provider at a time. Switching does not migrate stored keys.
          </span>
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          <label htmlFor={apiKeyField} className="field-label">
            API key
          </label>
          <span className="relative flex items-center">
            <input
              id={apiKeyField}
              aria-describedby={apiKeyNote}
              value={draft.apiKey}
              type={keyShown ? 'text' : 'password'}
              placeholder={keyStored && !removeKey ? `stored · …${saved.apiKeyLast4}` : 'sk-…'}
              disabled={!canStoreKey || removeKey}
              onChange={(event) => setDraft({ ...draft, apiKey: event.target.value })}
              {...SECRET_FIELD}
              className="field field-mono w-full pr-[62px] disabled:opacity-50"
            />
            <button
              type="button"
              onClick={() => setKeyShown((shown) => !shown)}
              disabled={!canStoreKey || removeKey}
              /*
                ⚠️ Smaller than `.btn-small` on purpose: it sits inside the
                input, and the class's 28px would not fit a 32px box.
              */
              className="btn-small focus-ring absolute right-[5px] min-h-6 cursor-pointer px-[7px] py-[3px] text-[11px] disabled:cursor-default disabled:opacity-50"
            >
              {keyShown ? 'Hide' : 'Show'}
            </button>
          </span>
          <span id={apiKeyNote} className="text-[11px] leading-[1.45]" style={{ color: 'var(--dim)' }}>
            {keyStored && !removeKey ? (
              <>
                Stored encrypted; only the last four characters are shown. Type to replace it, or{' '}
                <button
                  type="button"
                  onClick={() => setRemoveKey(true)}
                  className="prose-link focus-ring cursor-pointer"
                  style={{ color: 'var(--warn)' }}
                >
                  remove it
                </button>
                .
              </>
            ) : removeKey ? (
              <>
                The stored key will be removed on Save.{' '}
                <button
                  type="button"
                  onClick={() => setRemoveKey(false)}
                  className="prose-link focus-ring cursor-pointer"
                  style={{ color: 'var(--accent)' }}
                >
                  Keep it
                </button>
                .
              </>
            ) : (
              'Stored encrypted. Only the last four characters are shown after saving.'
            )}
          </span>
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          <label className="flex flex-col gap-1.5">
            <span className="field-label">Base URL</span>
            <input
              value={draft.baseUrl}
              placeholder={DEFAULT_BASE_URL[draft.provider] ?? 'https://…'}
              aria-describedby={baseUrlNote}
              onChange={(event) => setDraft({ ...draft, baseUrl: event.target.value })}
              {...SECRET_FIELD}
              className="field field-mono w-full"
            />
          </label>
          <span id={baseUrlNote} className="text-[11px] leading-[1.45]" style={{ color: 'var(--dim)' }}>
            {DEFAULT_BASE_URL[draft.provider]
              ? 'Leave blank for the provider’s own address. Change it only for a proxy or a self-hosted gateway.'
              : 'Required: where this provider answers. Plain http is allowed only on localhost.'}
          </span>
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          {draft.provider === 'anthropic' ? (
            <>
              <span id={modelLabelId} className="field-label">
                Model
              </span>
              <Select
                value={draft.model}
                options={CLAUDE_MODELS}
                onChange={(model) => setDraft({ ...draft, model })}
                labelledBy={modelLabelId}
              />
              <span className="text-[11px] leading-[1.45]" style={{ color: 'var(--dim)' }}>
                Sonnet handles translation well at a lower cost than Opus.
              </span>
            </>
          ) : (
            <>
              <label className="flex flex-col gap-1.5">
                <span className="field-label">Model</span>
                <input
                  value={draft.model}
                  placeholder="the model id, as the provider names it"
                  onChange={(event) => setDraft({ ...draft, model: event.target.value })}
                  {...SECRET_FIELD}
                  className="field field-mono w-full"
                />
              </label>
              <span className="text-[11px] leading-[1.45]" style={{ color: 'var(--dim)' }}>
                Typed rather than picked: this admin cannot vouch for another provider&rsquo;s
                model names.
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={!dirty || save.isPending || draft.model.trim() === ''}
            onClick={onSave}
            className="btn-accent focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save provider'}
          </button>
          <button
            type="button"
            /*
              Only once something is saved and usable: with no key the server
              answers "no key" without calling anybody, and a button whose only
              outcome is that sentence is a button that should not be lit.
            */
            disabled={!saved.configured || dirty || test.isPending}
            aria-describedby={test.data ? testResultId : undefined}
            onClick={() => test.mutate()}
            className="btn-outline focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
          >
            {test.isPending ? 'Asking…' : 'Send a test prompt'}
          </button>
          {dirty && saved.configured ? (
            <span className="text-[11px]" style={{ color: 'var(--dim)' }}>
              Save first — the test uses what is stored.
            </span>
          ) : null}
        </span>

        {save.isError ? (
          <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {saveMessage(save.error)}
          </span>
        ) : null}

        {test.data && !dirty ? (
          <span
            id={testResultId}
            role="status"
            className="flex items-center gap-2 text-[11.5px]"
            style={{ color: test.data.ok ? 'var(--ok)' : 'var(--warn)' }}
          >
            <span
              aria-hidden="true"
              className="h-[5px] w-[5px] flex-none rounded-full"
              style={{ background: 'currentColor' }}
            />
            {test.data.message}
          </span>
        ) : test.isError && !dirty ? (
          <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {saveMessage(test.error)}
          </span>
        ) : null}
      </div>
    </section>
  );
}

/** The design's status pill: a dot and a word in the tone's colour. */
function Pill({ tone, children }: { tone: 'ok' | 'warn'; children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px]"
      style={{ color: `var(--${tone})`, borderColor: 'var(--line2)' }}
    >
      <span aria-hidden="true" className="h-[5px] w-[5px] rounded-full" style={{ background: 'currentColor' }} />
      {children}
    </span>
  );
}

/** The design's dashed callout, for a state the person has to do something about. */
function Notice({ tone, children }: { tone: 'warn'; children: React.ReactNode }) {
  return (
    <span
      className="flex items-start gap-2.5 rounded-lg border border-dashed px-[11px] py-[9px] text-[11.5px] leading-[1.5]"
      style={{ background: 'var(--in)', borderColor: 'var(--line2)', color: 'var(--muted)' }}
    >
      <span
        aria-hidden="true"
        className="mt-[5px] h-[5px] w-[5px] flex-none rounded-full"
        style={{ background: `var(--${tone})` }}
      />
      <span>{children}</span>
    </span>
  );
}
